import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import ExerciseResults from "@components/exercise-results/exercise-results";
import { useMetaTags } from "@components/meta-tags/hooks/use-meta-tags";
import QueryHandler from "@components/query-handler/query-handler";
import WrapperRewardsModals from "@components/wrapper-rewards-modals/wrapper-rewards-modals";

import { useAppDispatch } from "@hooks/use-store";
import { formatUrlWithLocale } from "@helpers/format-url-with-locale";
import {
  getStoredRewardIds,
  rewardsModalContent,
  updateStoredRewardIds,
} from "@helpers/helpers";
import { Award, ExerciseCompleteData, IReward } from "@typings/global.types";

import {
  exerciseEntrypointApi,
  useGetExerciseEntrypointQuery,
} from "../../api/exercise-entrypoint";
import { exercisesApi } from "../../api/exercises";
import { useUpdateUserInfoMutation } from "../../api/user-info";

function WrapperExerciseResults() {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const intl = useIntl();
  const { setMetaTags } = useMetaTags();

  const { formatMessage } = useIntl();

  const { data } = useGetExerciseEntrypointQuery({
    exerciseId: 0,
  });

  const strixToday = data?.user?.strixToday;

  const [exerciseCompleteData, setExerciseCompleteData] =
    useState<ExerciseCompleteData | null>(null);
  const [rewardModalQueue, setRewardModalQueue] = useState<number[]>([]);
  const [currentModalType, setCurrentModalType] = useState<number | null>(null);

  const [title, setTitle] = useState<string>("");
  const hasNavigated = useRef(false);

  const [starData, setStarData] = useState<Award | null>(null);
  const [targetData, setTargetData] = useState<Award | null>(null);
  const [lightningData, setLightningData] = useState<Award | null>(null);
  const isRemoveAdsLinkVisible =
    !data?.user.plus && !window.reactApp.haveLicence;

  // змінюємо вправу
  const [
    editUser,
    {
      isLoading: editUserIsLoading,
      isError: editUserIsError,
      error: editUserError,
      isSuccess: editUserIsSuccess,
    },
  ] = useUpdateUserInfoMutation();

  // наступна вправа
  const handleNextExercises = () => {
    if (exerciseCompleteData?.blockExercise) {
      const userId = window.apiUserId;
      const url = formatUrlWithLocale(`/u${userId}/unlimited-typing/`);
      window.location.href = url;
      return;
    }

    if (exerciseCompleteData) {
      if (!strixToday) {
        // пройшов першу вправу дня
        window.location.href = formatUrlWithLocale(
          "/typing-tutor/challenge-complete/",
        );
      } else if (exerciseCompleteData.lastLesson) {
        // закінчився урок
        navigate(formatUrlWithLocale("/typing-tutor/lesson-complete/"));
      } else if (exerciseCompleteData.lastCourse) {
        // закінчився курс
        navigate(formatUrlWithLocale("/typing-tutor/course-complete/"));
      } else if (
        !exerciseCompleteData.lastLesson &&
        !exerciseCompleteData.lastCourse &&
        strixToday
      ) {
        editUser({
          params: {
            currentExerciseId: exerciseCompleteData.nextExerciseId,
          },
        });
      }
    }
  };

  const handlePrevExercises = () => {
    if (exerciseCompleteData) {
      editUser({
        params: {
          currentExerciseId: exerciseCompleteData.id,
        },
      });
    }
  };

  useEffect(() => {
    const sessionStorageData = window.sessionStorage.getItem(
      "exerciseCompleteData",
    );
    if (!sessionStorageData) {
      dispatch(exercisesApi.util.resetApiState());
      navigate(formatUrlWithLocale("/typing-tutor/"));
    } else {
      setExerciseCompleteData(JSON.parse(sessionStorageData));
    }

    if (sessionStorageData) {
      const sessionStorageDataParse = JSON.parse(sessionStorageData);
      // обробляємо дані з sessionStorage
      if (sessionStorageDataParse.hasBetterResults) {
        setTitle(formatMessage({ id: "already-have-better-results" }));
      } else {
        setTitle(formatMessage({ id: "exercise-finished" }));
      }
      setStarData(sessionStorageDataParse.starData);
      setTargetData(sessionStorageDataParse.targetData);
      setLightningData(sessionStorageDataParse.lightningData);
    }
  }, [dispatch, navigate, formatMessage]);

  useEffect(() => {
    if (editUserIsSuccess && !hasNavigated.current) {
      hasNavigated.current = true;
      if (!strixToday) {
        window.location.href = formatUrlWithLocale(
          "/typing-tutor/challenge-complete/",
        );
      } else {
        dispatch(exercisesApi.util.resetApiState());
        dispatch(exerciseEntrypointApi.util.resetApiState());
        navigate(formatUrlWithLocale("/typing-tutor/"));
      }
    }
  }, [dispatch, editUserIsSuccess, navigate, strixToday]);

  useEffect(() => {
    if (exerciseCompleteData && data) {
      const storedRewardIds = getStoredRewardIds(data.user.id);
      const currentRewardIds = exerciseCompleteData.rewards.map(
        (reward: IReward) => reward.id,
      );

      if (!storedRewardIds) {
        updateStoredRewardIds(currentRewardIds, data.user.id);
        return;
      }

      const newRewardIds = currentRewardIds.filter(
        (id) => !storedRewardIds.includes(id) && [1, 2, 3].includes(id),
      );

      if (newRewardIds.length > 0) {
        updateStoredRewardIds(
          [...storedRewardIds, ...newRewardIds],
          data.user.id,
        );
      }

      const validRewardIds = newRewardIds.filter(
        (id) => rewardsModalContent[id as keyof typeof rewardsModalContent],
      );
      setRewardModalQueue(validRewardIds);
    }
  }, [exerciseCompleteData, data]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (rewardModalQueue.length > 0 && currentModalType === null) {
      const timer = setTimeout(() => {
        setCurrentModalType(rewardModalQueue[0]);
        setRewardModalQueue((prevQueue) => prevQueue.slice(1));
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [rewardModalQueue, currentModalType]);

  const handleCloseRewardModal = () => {
    if (rewardModalQueue.length > 0) {
      setCurrentModalType(rewardModalQueue[0]);
      setRewardModalQueue((prevQueue) => prevQueue.slice(1));
    } else {
      setCurrentModalType(null);
    }
  };

  if (!editUserIsLoading && !editUserIsError && !editUserIsSuccess) {
    setMetaTags({
      title: intl.formatMessage({ id: "meta-title-finish-authorized" }),
      description: intl.formatMessage({
        id: "meta-description-finish-authorized",
      }),
      robots: "noindex",
    });
  }

  return (
    <QueryHandler
      // isLoading={editUserIsLoading}
      isError={editUserIsError}
      errorMessage={editUserError}
    >
      {exerciseCompleteData && starData && targetData && lightningData ? (
        <ExerciseResults
          title={title}
          star={starData}
          target={targetData}
          lightning={lightningData}
          errors={exerciseCompleteData.mistake}
          speed={exerciseCompleteData.speed}
          coins={exerciseCompleteData.coins}
          coinsBeforeStart={exerciseCompleteData.coinsBeforeStart}
          coinsForLesson={exerciseCompleteData.lessonCoins}
          gameMode={exerciseCompleteData.gameMode}
          speedRatio={exerciseCompleteData.speedRatio}
          isRemoveAdsLinkVisible={isRemoveAdsLinkVisible}
          onClickPrevExercises={handlePrevExercises}
          onClickNextExercises={handleNextExercises}
          exercisesList={exerciseCompleteData.exercises}
          lastExerciseId={exerciseCompleteData.lastExerciseId}
          currentExerciseId={exerciseCompleteData.id}
        />
      ) : null}
      {data?.user.rewards && (
        <WrapperRewardsModals
          closeModalHandler={handleCloseRewardModal}
          modalType={currentModalType}
          queueModal={rewardModalQueue.length}
        />
      )}
    </QueryHandler>
  );
}

export default WrapperExerciseResults;
